import { useId, type ReactNode } from 'react';
import { cx } from './cx';

export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  invalid: boolean;
  required: boolean;
}

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** For layout, such as `eb-span-all` to fill a whole row of a form grid. */
  className?: string;
  /** Receives the ids to wire onto the control: `{...props}` on an Input or Select. */
  children: ReactNode | ((props: FieldControlProps) => ReactNode);
}

export function Field({ label, hint, error, required = false, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('eb-field', className)}>
      <label className="eb-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="eb-field__req" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {typeof children === 'function'
        ? children({ id, 'aria-describedby': describedBy, invalid: Boolean(error), required })
        : children}
      {hint ? (
        <div id={hintId} className="eb-field__hint">
          {hint}
        </div>
      ) : null}
      {error ? (
        <div id={errorId} className="eb-field__error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
